'use client'

import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react'

type MathJaxStatus = 'loading' | 'ready' | 'error'

const MathJaxContext = createContext<{
  status: MathJaxStatus
  isReady: boolean
}>({
  status: 'loading',
  isReady: false,
})

export const useMathJax = () => useContext(MathJaxContext)

export const MathJaxProvider = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')

  useEffect(() => {
    if (typeof window === 'undefined') {
      setStatus('ready')
      return
    }

    // Check if MathJax is already loaded and configured
    if (window.MathJax?.startup?.promise) {
      window.MathJax.startup.promise
        .then(() => setStatus('ready'))
        .catch(() => setStatus('error'))
      return
    }

    const script = document.createElement('script')
    script.src = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js'
    script.async = true
    script.id = 'mathjax-script'

    const handleLoad = () => {
      if (!window.MathJax) {
        setStatus('error')
        return
      }

      // MathJax Configuration
      window.MathJax = {
        ...window.MathJax,
        tex: {
          inlineMath: [['\\(', '\\)']],
          displayMath: [['\\[', '\\]']],
          processEscapes: true,
        },
        options: {
          skipHtmlTags: ['script', 'noscript', 'style', 'textarea', 'pre'],
          ignoreHtmlClass: 'latex-content|math-raw',
        },
      }

      // Wait for MathJax to be fully ready
      const readyPromise = window.MathJax.startup?.promise || Promise.resolve()
      readyPromise
        .then(() => {
          // console.log('MathJax fully loaded and configured');
          setStatus('ready')
        })
        .catch((err) => {
          console.error('MathJax initialization failed:', err)
          setStatus('error')
        })
    }

    script.onload = handleLoad
    script.onerror = () => {
      console.error('Failed to load MathJax script')
      setStatus('error')
    }

    document.head.appendChild(script)

    return () => {
      script.removeEventListener('load', handleLoad)
      script.remove()
    }
  }, [])

  // useEffect(() => {
  //   // console.log('MathJax loading status:', status);
  //   if (status === 'ready') {
  //     // console.log('MathJax object:', window.MathJax);
  //   }
  // }, [status]);

  return (
    <MathJaxContext.Provider value={{ status, isReady: status === 'ready' }}>
      {children}
    </MathJaxContext.Provider>
  )
}
